import Button from '@mui/material/Button'
import { CountriesData, LanguagesData } from 'api/UtilsAPI'
import colors from 'constants/colors'
import { AlignmentType, BorderStyle, Document, Footer, Packer, PageNumber, Paragraph, Table, TextRun } from 'docx'
import { saveAs } from 'file-saver'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  IPersonCertificate,
  IPersonCompetence,
  ICourse,
  IEducation,
  IEmployer,
  ILanguageSkill,
  IPersonRole,
  IPersonIndustry,
  IPersonSkill,
  IProject,
  IReference,
  IUrl,
} from 'types/cvsInterfaces'
import { IItemVisibility, ILayout, IOrganizationColors } from 'types/layoutInterfaces'
import { chooseDBTranslation } from 'utils/translations'
import About from './About'
import Education from './Education'
import Employment from './Employment'
import Language from './Language'
import Project from './Project'
import Reference from './Reference'
import LeisureActivity from './LeisureActivity'
import Skill from './Skill'
import Top from './Top'
import Url from './Url'
import defaultImg from '../PDFrender/default.jpg'
import { CVData } from 'types/printInterfaces'
import { useUser } from 'hooks'
import { useNotificationPopup } from 'Components/reusable/Notification'
import { nullsToUndefined, toDocxColor } from './sanitize'
import { docxFontName, completeFontEmbedding, loadDocxFonts } from './fonts'
import { fontSize, pageMargin } from './pageLayout'
import { toRoundImage } from './roundImage'
import { findHideSetting } from '../hideSettings'

/** @notExported */
interface IDocxRenderProps {
  /** CV data */
  data: CVData
  /** Layout */
  layout: ILayout
  /** Countries data */
  countries: CountriesData
  /** Profile image */
  profileImage?: string
  /** Organization logo */
  orgLogo?: string
  /** Organization colors */
  orgColors: IOrganizationColors
  /** Languages data */
  languages: LanguagesData
}

/**
 * DOCX render component.
 *
 * @returns DOCX render component.
 * @notExported
 */
const DOCXrender: React.FC<IDocxRenderProps> = ({
  data: rawData,
  layout,
  countries,
  profileImage,
  orgLogo,
  orgColors,
  languages,
}) => {
  const { t, i18n } = useTranslation()
  const { features } = useUser()
  const { setNotificationPopup } = useNotificationPopup()
  // docx crashes on null texts, which the database returns for empty fields
  const data = nullsToUndefined(rawData)

  const createHeader = (title: string) => {
    return new Paragraph({
      text: t(title),
      style: 'CaleoHeadings1',
      border: {
        bottom: {
          style: BorderStyle.SINGLE,
          color: '#000000',
          space: 1,
          size: 16,
        },
      },
    })
  }

  const handleLayout = async (): Promise<CVData> => {
    // filter visibilities according to item type
    let degreeVisibilities: IItemVisibility[] = []
    let courseVisibilities: IItemVisibility[] = []
    let certificateVisibilities: IItemVisibility[] = []
    let competenceVisibilities: IItemVisibility[] = []
    let skillVisibilities: IItemVisibility[] = []
    let industryVisibilities: IItemVisibility[] = []
    let roleVisibilities: IItemVisibility[] = []
    let employmentVisibilities: IItemVisibility[] = []
    let projectVisibilities: IItemVisibility[] = []
    let referenceVisibilities: IItemVisibility[] = []
    let languageVisibilities: IItemVisibility[] = []
    let urlVisibilities: IItemVisibility[] = []
    let leisureActivityVisibilities: IItemVisibility[] = []

    const aboutVisibility: IItemVisibility | undefined = layout.ItemVisibilities.find(
      (visibility: IItemVisibility) => visibility.type === 'about'
    )
    degreeVisibilities = layout.ItemVisibilities.filter((visibility: IItemVisibility) => visibility.type === 'degrees')
    courseVisibilities = layout.ItemVisibilities.filter((visibility: IItemVisibility) => visibility.type === 'courses')
    certificateVisibilities = layout.ItemVisibilities.filter(
      (visibility: IItemVisibility) => visibility.type === 'certificates'
    )
    competenceVisibilities = layout.ItemVisibilities.filter(
      (visibility: IItemVisibility) => visibility.type === 'competences'
    )
    skillVisibilities = layout.ItemVisibilities.filter((visibility: IItemVisibility) => visibility.type === 'skills')
    industryVisibilities = layout.ItemVisibilities.filter(
      (visibility: IItemVisibility) => visibility.type === 'industries'
    )
    roleVisibilities = layout.ItemVisibilities.filter((visibility: IItemVisibility) => visibility.type === 'roles')
    employmentVisibilities = layout.ItemVisibilities.filter(
      (visibility: IItemVisibility) => visibility.type === 'employments'
    )
    projectVisibilities = layout.ItemVisibilities.filter(
      (visibility: IItemVisibility) => visibility.type === 'projects'
    )
    referenceVisibilities = layout.ItemVisibilities.filter(
      (visibility: IItemVisibility) => visibility.type === 'references'
    )
    languageVisibilities = layout.ItemVisibilities.filter(
      (visibility: IItemVisibility) => visibility.type === 'languages'
    )
    urlVisibilities = layout.ItemVisibilities.filter((visibility: IItemVisibility) => visibility.type === 'urls')
    leisureActivityVisibilities = layout.ItemVisibilities.filter(
      (visibility: IItemVisibility) => visibility.type === 'leisureActivities'
    )

    // filter hidden data according to layout
    let about = {
      ...data.about,
      hidden: true,
    }
    if (!aboutVisibility || (aboutVisibility && aboutVisibility.hidden === false)) {
      about = { ...data.about, hidden: false }
    }

    let degrees: IEducation[] = []
    degrees = data.degrees.filter(degree => {
      const visibility = degreeVisibilities.find(
        visibility => visibility.itemId === degree.id && visibility.hidden === true
      )
      if (!visibility) {
        return degree
      }
    })

    let courses: ICourse[] = []
    courses = data.courses.filter(course => {
      const visibility = courseVisibilities.find(
        visibility => visibility.itemId === course.id && visibility.hidden === true
      )
      if (!visibility) {
        return course
      }
    })

    let certificates: IPersonCertificate[] = []
    certificates = data.personCertificates.filter(certificate => {
      const visibility = certificateVisibilities.find(
        visibility => visibility.itemId === certificate.id && visibility.hidden === true
      )
      if (!visibility) {
        return certificate
      }
    })

    let competences: IPersonCompetence[] = []
    competences = data.personCompetences.filter(competence => {
      if (features.includes('competences')) {
        const visibility = competenceVisibilities.find(
          visibility => visibility.itemId === competence.id && visibility.hidden === true
        )
        if (!visibility) {
          return competence
        }
      } else {
        return false
      }
    })

    let skills: IPersonSkill[] = []
    skills = data.skills.filter(skill => {
      const visibility = skillVisibilities.find(
        visibility => visibility.itemId === skill.id && visibility.hidden === true
      )
      if (!visibility) {
        return skill
      }
    })

    let industries: IPersonIndustry[] = []
    industries = data.industries.filter(industry => {
      const visibility = industryVisibilities.find(
        visibility => visibility.itemId === industry.id && visibility.hidden === true
      )
      if (!visibility) {
        return industry
      }
    })

    let roles: IPersonRole[] = []
    roles = data.roles.filter(role => {
      const visibility = roleVisibilities.find(
        visibility => visibility.itemId === role.id && visibility.hidden === true
      )
      if (!visibility) {
        return role
      }
    })

    let employments: IEmployer[] = []
    employments = data.employments.filter(employment => {
      const visibility = employmentVisibilities.find(
        visibility => visibility.itemId === employment.id && visibility.hidden === true
      )
      if (!visibility) {
        return employment
      }
    })

    let projects: IProject[] = []
    projects = data.projects.filter(project => {
      const visibility = projectVisibilities.find(
        visibility => visibility.itemId === project.id && visibility.hidden === true
      )
      if (!visibility) {
        return project
      }
    })

    let references: IReference[] = []
    references = data.references.filter(reference => {
      const visibility = referenceVisibilities.find(
        visibility => visibility.itemId === reference.id && visibility.hidden === true
      )
      if (!visibility) {
        return reference
      }
    })

    let languages: ILanguageSkill[] = []
    languages = data.languages.filter(language => {
      const visibility = languageVisibilities.find(
        visibility => visibility.itemId === language.id && visibility.hidden === true
      )
      if (!visibility) {
        return language
      }
    })

    let urls: IUrl[] = []
    urls = data.urls.filter(url => {
      const visibility = urlVisibilities.find(visibility => visibility.itemId === url.id && visibility.hidden === true)
      if (!visibility) {
        return url
      }
    })

    const leisureActivities = (data.leisureActivities ?? []).filter(leisureActivity => {
      const visibility = leisureActivityVisibilities.find(
        visibility => visibility.itemId === leisureActivity.id && visibility.hidden === true
      )
      if (!visibility) {
        return leisureActivity
      }
    })

    return {
      about,
      degrees,
      courses,
      personCertificates: certificates,
      personCompetences: competences,
      skills,
      industries,
      roles,
      employments,
      projects,
      references,
      languages,
      urls,
      leisureActivities,
    }
  }

  const generate = async () => {
    try {
      await createDocx()
    } catch (error) {
      console.error(error)
      setNotificationPopup({ message: t('print.downloadDocxError'), type: 'error' })
    }
  }

  const createDocx = async () => {
    const cvData = await handleLayout()
    const cvParts: (Table | Paragraph)[] = []

    let image: ArrayBuffer | undefined, logo: ArrayBuffer | undefined

    if (profileImage) {
      image = await fetch(profileImage)
        .then(res => res.arrayBuffer())
        .catch((error: unknown) => {
          console.error(error)
          return undefined
        })
    }
    if (orgLogo) {
      logo = await fetch(orgLogo)
        .then(res => res.arrayBuffer())
        .catch((error: unknown) => {
          console.error(error)
          return undefined
        })
    }

    const tempImg = await fetch(defaultImg)
      .then(res => res.arrayBuffer())
      .catch((error: unknown) => {
        console.error(error)
        return undefined
      })

    // the profile image is round like in the PDF, also when the default image is used
    const profile = image ?? tempImg
    const roundImage = profile ? await toRoundImage(profile) : undefined

    cvParts.push(Top(i18n, data, countries, roundImage, logo, orgColors, layout, tempImg))
    if (chooseDBTranslation(i18n, cvData.about).about && cvData.about.hidden !== true) {
      cvParts.push(createHeader('print.about.title'), About(i18n, cvData))
    }
    if (cvData.skills.length) {
      cvParts.push(
        createHeader('print.skills.title'),
        Skill(i18n, cvData, 'skills', t, layout.hideLastUsed, layout.hideSkillExperience)
      )
    }
    if (cvData.industries.length) {
      cvParts.push(
        createHeader('print.industries.title'),
        Skill(i18n, cvData, 'industries', t, layout.hideLastUsed, layout.hideSkillExperience)
      )
    }
    if (cvData.roles.length) {
      cvParts.push(
        createHeader('print.roles.title'),
        Skill(i18n, cvData, 'roles', t, layout.hideLastUsed, layout.hideSkillExperience)
      )
    }
    if (cvData.employments.length) {
      cvParts.push(
        createHeader('print.employments.title'),
        ...Employment(i18n, cvData, t, findHideSetting(layout, 'employments'))
      )
    }
    if (cvData.projects.length) {
      cvParts.push(
        createHeader('print.projects.title'),
        ...Project(i18n, cvData, t, findHideSetting(layout, 'projects'))
      )
    }
    if (cvData.degrees.length) {
      cvParts.push(
        createHeader('print.degrees.title'),
        ...Education(i18n, cvData, 'degrees', layout.showCertificateExpiry, t, findHideSetting(layout, 'degrees'))
      )
    }
    if (cvData.courses.length) {
      cvParts.push(
        createHeader('print.courses.title'),
        ...Education(i18n, cvData, 'courses', layout.showCertificateExpiry, t, findHideSetting(layout, 'courses'))
      )
    }
    if (cvData.personCertificates.length) {
      cvParts.push(
        createHeader('print.certificates.title'),
        ...Education(
          i18n,
          cvData,
          'certificates',
          layout.showCertificateExpiry,
          t,
          findHideSetting(layout, 'certificates')
        )
      )
    }
    if (cvData.personCompetences.length && features.includes('competences')) {
      cvParts.push(
        createHeader('print.competences.title'),
        ...Education(
          i18n,
          cvData,
          'competences',
          layout.showCertificateExpiry,
          t,
          findHideSetting(layout, 'competences')
        )
      )
    }
    if (cvData.references.length) {
      cvParts.push(
        createHeader('print.reference.title'),
        ...Reference(i18n, cvData, t, findHideSetting(layout, 'references'))
      )
    }
    if (cvData.leisureActivities.length) {
      cvParts.push(
        createHeader('print.leisureActivities.title'),
        ...LeisureActivity(i18n, cvData, t, findHideSetting(layout, 'leisureActivities'))
      )
    }
    if (cvData.languages.length) {
      cvParts.push(createHeader('print.languages.title'), Language(cvData, languages, t))
    }
    if (cvData.urls.length) {
      cvParts.push(createHeader('print.links.title'), ...Url(i18n, cvData))
    }

    const fonts = await loadDocxFonts()
    const doc = new Document({
      creator: 'Caleo',
      fonts: [{ name: docxFontName, data: fonts.regular as Buffer }],
      styles: {
        paragraphStyles: [
          {
            id: 'CaleoHeadings1',
            name: 'Caleo Heading 1',
            basedOn: 'Normal',
            next: 'CaleoNormal',
            quickFormat: true,
            // the same size and spacing as the PDF section titles
            run: {
              size: 38,
              color: toDocxColor(orgColors?.accentColor, colors.primary),
              font: {
                name: 'Raleway',
              },
              bold: true,
            },
            paragraph: {
              keepNext: true,
              spacing: {
                after: 160,
                before: 360,
              },
            },
          },
          {
            id: 'GrayText',
            name: 'Gray Text',
            basedOn: 'Normal',
            next: 'CaleoNormal',
            quickFormat: true,
            run: {
              color: colors.description,
              font: {
                name: 'Raleway',
              },
            },
            paragraph: {
              spacing: {
                before: 200,
                after: 200,
              },
            },
          },
          {
            id: 'CaleoNormal',
            name: 'Caleo Normal',
            basedOn: 'Normal',
            next: 'CaleoNormal',
            quickFormat: true,
            run: {
              size: fontSize.body,
              font: {
                name: 'Raleway',
              },
            },
            // roughly the line height of the PDF
            paragraph: {
              spacing: {
                line: 360,
              },
            },
          },
        ],
      },
      sections: [
        {
          properties: {
            // the first page has no page number, like in the PDF
            titlePage: true,
            page: {
              margin: {
                top: pageMargin,
                right: pageMargin,
                bottom: pageMargin,
                left: pageMargin,
                header: pageMargin / 2,
                footer: pageMargin / 2,
              },
            },
          },
          footers: {
            first: new Footer({ children: [] }),
            default: new Footer({
              children: [
                new Paragraph({
                  style: 'CaleoNormal',
                  spacing: { line: 240 },
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({
                      children: [PageNumber.CURRENT],
                      size: fontSize.small,
                      color: colors.gray,
                    }),
                  ],
                }),
              ],
            }),
          },
          children: cvParts,
        },
      ],
    })

    const blob = await completeFontEmbedding(await Packer.toBlob(doc), fonts.bold)
    const organizationName = chooseDBTranslation(i18n, data.about.Organization)?.name ?? 'freelancer'
    const personName = [data.about.firstName, data.about.lastName]
      .map(name => name?.trim())
      .filter(Boolean)
      .join('_')
    const filename = `${organizationName}_CV_${personName ? `${personName}_` : ''}${new Date(
      Date.now()
    ).toLocaleDateString()}.docx`

    saveAs(blob, filename)
  }

  return (
    // button to create docx file
    <Button data-testid="button-print-download-docx" onClick={generate}>
      {t('print.downloadDocx')}
    </Button>
  )
}

export default DOCXrender
